import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import NcModal from "shared/NcModal/NcModal";
import { IProduct } from "Interfaces/interface.productsapi";
import {
  ArrowLeft,
  ArrowRight,
  ImagesIcon,
  Info,
  InfoIcon,
  NotebookText,
  Package,
  Plus,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
  fetchUserLogos,
  selectFetchedUserLogos,
  selectUserLogoLoaded,
  selectUserLogoLoading,
} from "store/slices/logosSlice";
import { motion } from "framer-motion";
import LogoUploadeModel from "shared/LogoUploadModal/LogoUploadModal";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import GoBackBtn from "shared/GoBackBtn/GoBackBtn";
import { selectUser } from "store/slices/authSlice";
import {
  ICatalogData,
  ICatalogGridConfig,
  ICatalogPayload,
  ICatalogTemplates,
} from "Interfaces/interface.catalog";
import catalogService from "services/catalogService";
import { catalogGenerationMessages, PlatformServices } from "data/tools.enum";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import GenerationModalContent from "shared/ToolsGenerationModal/GenerationModalContent";
import {
  fetchUserCatalogs,
  selectUserCatalogs,
} from "store/slices/catalogSlice";
import { ErrorMessage, Field, Form, Formik } from "formik";
import {
  COMPANY_NAME_PATTERN,
  DESCRIPTION_PATTERN,
  TITLE_PATTERN,
} from "utils/validationRegex";
import {
  getProductAttributeString,
  getProductNameWithAttributes,
} from "utils/AttributeHandlers";
import { restrictGenerationLimit } from "utils/toolsUtilities";
import Tippy from "@tippyjs/react";
import { extractErrorMessage } from "utils/ErrorHandlers";
import DeviceFingerprint from "utils/deviceFingerprint";
import { generatePdfBlob, generateChunkedPdfBlob } from "@/utils/pdfGenerator";
import { environment } from "@/environments/environment";
import he from "he";
import { ITEM_TYPES } from "@/utils/itemTypeHelpers";
import { Badge } from "@/components/UI/Badge";

const getThemeName = (themeCode: string): string =>
  themeCode
    ? themeCode
      .replace(/-\d+$/, "")
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || "Default"
    : "Default";

const validationSchema = (values: any) => {
  const errors: Partial<any> = {};

  if (!values.companyName) {
    errors.companyName = "Please enter your company name";
  } else if (!COMPANY_NAME_PATTERN.test(values.companyName)) {
    errors.companyName = "Please enter a valid company name";
  } else if (values.companyName.length > 50) {
    errors.companyName = "Company name must be 50 characters or less";
  }

  if (!values.catalogTitle) {
    errors.catalogTitle = "Please enter catalog title";
  } else if (!TITLE_PATTERN.test(values.catalogTitle)) {
    errors.catalogTitle =
      "Catalog title should not include special characters and must be greater than 2 characters";
  } else if (values.catalogTitle.length > 50) {
    errors.catalogTitle = "Catalog title must be 50 characters or less";
  }

  if (values.description && !DESCRIPTION_PATTERN.test(values.description)) {
    errors.description = "Description contains invalid characters";
  } else if (values.description && values.description.length > 255) {
    errors.description = "Description must be 255 characters or less";
  }

  return errors;
};

const CatalogGenerationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState<ICatalogTemplates[]>([]);
  const userLogos = useAppSelector(selectFetchedUserLogos);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [selectedThemeCode, setSelectedThemeCode] = useState<string>("");
  const user = useAppSelector(selectUser);
  const [selectedGridConfig, setSelectedGridConfig] =
    useState<ICatalogGridConfig>({
      columns: 2,
      rows: 2,
    });
  const [customizationData, setCustomizationData] = useState<
    Partial<ICatalogPayload>
  >({
    companyName: user?.company || "",
    catalogTitle: "",
    description: "",
    footerText: "",
    displayPrice: false,
    themeCode: "default",
    gridConfig: { columns: 2, rows: 2 },
    logo: "",
  });
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allThemes, setAllThemes] = useState<string[]>(["default"]);
  const [messageIndex, setMessageIndex] = useState(0);
  const isLogosLoaded = useAppSelector(selectUserLogoLoaded);
  const isLogosLoading = useAppSelector(selectUserLogoLoading);
  const BASE_URL = environment.BASE_URL;
  const [newPrices, setNewPrices] = React.useState<number[]>(
    () =>
      selectedProducts?.map((p) => Number(p.variants?.[0]?.price || 0)) ?? []
  );

  const closeAddLogoDialogue = () => {
    setLogoModalVisible(false);
  };
  const generatedCatalogs = useAppSelector(selectUserCatalogs);
  const is360Customizer = user?.emailAddress?.endsWith("@360customizer.com");
  const [isStatus409, setIsStatus409] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setCustomizationData((prev) => ({
        ...prev,
        companyName: user.company || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (location.state?.products) {
      const productsFromCart = location.state.products.map(
        (item: any, index: number) => ({
          id: item.id,
          name: item.name || "Unknown Product",
          description: item.description || "",
          price: item.price,
          variants: [
            {
              attributes: item.attributes,
              price: item.price || 0,
              sku: item.sku || "",
            },
          ],
          categoryId: item.categoryId || "",
          images: item.images || "",
          brand: item.brand || "",
          itemType: item.itemType || "",
          productsCount: item.productsCount || 0,
        })
      );
      setSelectedProducts(productsFromCart);
      setNewPrices(
        productsFromCart.map((item: any) => parseFloat(item.price) || 0)
      );
    }
  }, [location.state]);

  useEffect(() => {
    if (templates.length && templates[0].previewOptions?.length) {
      const firstTheme = templates[0].previewOptions[0].themeCode;
      setSelectedThemeCode(firstTheme);
      setCustomizationData((prev) => ({ ...prev, themeCode: firstTheme }));
    }
  }, [templates]);

  const handleAddLogo = () => {
    setLogoModalVisible(true);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const data = await catalogService.fetchTemplates();
        setTemplates(Array.isArray(data) ? data : []);
        const themes = new Set<string>(["default"]);
        data.forEach((template) =>
          template.previewOptions.forEach((option) =>
            themes.add(option.themeCode || "default")
          )
        );
        setAllThemes(Array.from(themes));
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        toast.error("Failed to load templates. Please try again.");
        setError(
          `Unable to load templates: ${message}. Please try again later.`
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const isStepValid = (errors?: Partial<any>) => {
    switch (step) {
      case 1:
        return !!selectedTemplateId;
      case 2:
        // Compute errors using validationSchema for full validation
        const valuesForValidation = {
          companyName: customizationData.companyName,
          catalogTitle: customizationData.catalogTitle,
          description: customizationData.description,
        };
        const computedErrors = validationSchema(valuesForValidation);
        return Object.keys(computedErrors).length === 0;
      case 3:
        return (
          selectedProducts.length > 0 &&
          (is360Customizer
            ? newPrices.every((price) => !Number.isNaN(price) && price > 0)
            : true)
        );
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!isLogosLoaded) {
      dispatch(fetchUserLogos());
    }
  }, [dispatch, isLogosLoaded]);

  const selectLogo = (logo: any) => {
    if (logo) {
      setCustomizationData((prev) => ({
        ...prev,
        logo: logo.logo,
      }));
    }
    setSelectedLogoId(logo.id);
  };

  // Converts text to sentence case (first letter uppercase, rest lowercase)
  const toSentenceCase = (str: string): string => {
    if (!str) return "";
    const cleaned = str.toLowerCase().trim();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  // Removes HTML tags, decodes entities, and cleans whitespace


  const toTitleCase = (str: any): string => {
    if (typeof str !== "string") return ""; // Safely handle non-string values
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Truncate safely for consistent PDF layout
  const truncate = (str: string, max: number): string =>
    str.length > max ? str.slice(0, max - 3).trim() + "..." : str;

  const generatePDF = async () => {
    if (
      !restrictGenerationLimit(
        PlatformServices.CATALOG,
        generatedCatalogs.length,
        is360Customizer
      )
    )
      return;

    if (is360Customizer && newPrices.some((p) => isNaN(p) || p <= 0)) {
      toast.error("All custom prices must be valid positive numbers.");
      return;
    }

    if (!isStepValid()) {
      toast.error(
        "Please complete all required fields and select at least one product."
      );
      return;
    }

    let messageInterval: ReturnType<typeof setInterval> | null = null;

    try {
      setIsModalOpen(true);
      setIsDownloading(true);
      setError(null);
      setMessageIndex(0);

      /* ---------- 1. Build payload ---------- */

      let hasAnyProductPriceIncreased = false;
      if (is360Customizer) {
        selectedProducts.forEach((p, i) => {
          const originalPrice = Number(p.variants?.[0]?.price || 0);
          const newPrice = newPrices[i] ?? originalPrice;
          if (newPrice > originalPrice) {
            hasAnyProductPriceIncreased = true;
          }
        });
      }

      const sanitizedProducts = selectedProducts.map((p, i) => {
        const rawName = `${p.name || ""}`.trim();
        const attributes = getProductAttributeString(p);
        const rawDescription = p.description || "";

        const productData: any = {
          id: p.id,
          name: truncate(toSentenceCase(rawName), 60),
          description: rawDescription,
          price: newPrices[i] ?? Number(p.variants?.[0]?.price || 0),
          images: p.images,
          category: p.categoryId || "",
          brand: toTitleCase(p.brand),
          itemType: p.itemType,
          productsCount: p.productsCount || 0,
          attributes: attributes, // Add attributes separately
        };

        if (!hasAnyProductPriceIncreased) {
          productData.productUrl =
            p.itemType === ITEM_TYPES.PRODUCT_GROUP
              ? `${BASE_URL}/kit/${p.id}`
              : `${BASE_URL}/product/${p.id}`;
        }

        return productData;
      });

      const fingerprint = new DeviceFingerprint();
      const deviceFingerprint =
        await fingerprint.generateUniqueCatalogFingerprint();

      const payload: ICatalogData = {
        selectedTemplateId,
        customizationData: {
          ...customizationData,
          themeCode: selectedThemeCode,
          gridConfig: selectedGridConfig,
        },
        products: sanitizedProducts,
        createdAt: new Date(),
        updatedAt: new Date(),
        deviceFingerprint,
      };

      /* ---------- 2. Rotating messages ---------- */
      messageInterval = setInterval(() => {
        setMessageIndex((prev) =>
          prev + 1 < catalogGenerationMessages.length ? prev + 1 : 0
        );
      }, 2000);

      /* ---------- 3. Get HTML from BE ---------- */
      const response = await catalogService.generatePDF(payload);
      const { catalogHtml, deviceFingerprint: respFingerprint } = response;
      const finalFingerprint = respFingerprint || deviceFingerprint;

      let pdfBlob: Blob;

      if (catalogHtml.isChunked) {
        pdfBlob = await generateChunkedPdfBlob({
          frontHtml: catalogHtml.frontHtml || "",
          productChunks: catalogHtml.productChunks || [],
          lastHtml: catalogHtml.lastHtml || "",
        });
      } else {
        const html = catalogHtml.html || (catalogHtml as unknown as string);
        if (!html) throw new Error("No HTML returned from server.");
        pdfBlob = await generatePdfBlob(html, { format: "a4" });
      }

      /* ---------- 5. TRIGGER DOWNLOAD ---------- */
      const fileName = `${customizationData?.catalogTitle?.trim() || "Catalog"
        }.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      /* ---------- 6. Upload to webhook ---------- */
      const catalogName = customizationData?.catalogTitle?.trim() || "Catalog";

      await catalogService.uploadGeneratedCatalog({
        catalogName,
        deviceFingerprint: finalFingerprint,
        catalogBlob: pdfBlob,
      });

      /* ---------- 7. Store pending fingerprint ---------- */
      const pending =
        JSON.parse(
          localStorage.getItem("pendingCatalogFingerprints") || "[]"
        ) || [];
      if (!pending.includes(finalFingerprint)) {
        pending.push(finalFingerprint);
        localStorage.setItem(
          "pendingCatalogFingerprints",
          JSON.stringify(pending)
        );
      }

      dispatch(fetchUserCatalogs());

      toast.success("Catalog generated successfully!");
    } catch (err: any) {
      console.error("[CatalogGenerator] Error:", err);
      const msg = extractErrorMessage(err);
      setError(msg || "Something went wrong. Please try again.");
      toast.error(msg || "Failed to generate catalog.");
    } finally {
      if (messageInterval) clearInterval(messageInterval);
      setIsDownloading(false);
      setIsModalOpen(false);
    }
  };

  const nextStep = () => {
    if (step === 2) {
      const valuesForValidation = {
        companyName: customizationData.companyName,
        catalogTitle: customizationData.catalogTitle,
        description: customizationData.description,
      };
      const computedErrors = validationSchema(valuesForValidation);
      if (Object.keys(computedErrors).length > 0) {
        const firstErrorKey = Object.keys(computedErrors)[0] as string;
        toast.error(
          computedErrors[firstErrorKey] || "Please fix the errors above."
        );
        return;
      }
    }

    if (isStepValid()) {
      setStep((prev) => Math.min(prev + 1, 3));
    } else {
      toast.error("Please complete all required fields.");
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleErrorState = () => {
    if (isStatus409) {
      navigate("/profile");
    } else {
      window.location.reload();
    }
  };

  const [priceErrors, setPriceErrors] = React.useState<(string | null)[]>(() =>
    (selectedProducts || []).map(() => null)
  );

  // helper: sanitize - only digits, max 5 chars
  const sanitizePriceString = (raw: string) => {
    // remove non-digits
    const digits = raw.replace(/\D/g, "");
    // limit to 5 digits
    return digits.slice(0, 5);
  };

  const handlePriceChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value ?? "";
      const sanitized = sanitizePriceString(raw);

      // if sanitized is empty treat as NaN (so UI can reflect empty)
      const numericVal = sanitized === "" ? NaN : Number(sanitized);

      setNewPrices((prev) => {
        const copy = [...(prev || [])];
        copy[index] = Number.isNaN(numericVal) ? NaN : numericVal;
        return copy;
      });

      // validate: empty is allowed temporarily; check for <=0 only if numeric
      let err: string | null = null;
      if (sanitized.length > 5) err = "Max 5 digits allowed";
      else if (!Number.isNaN(numericVal) && numericVal <= 0) {
        err = "Price must be greater than 0";
      }
      // Note: No error for empty/NaN here—handle on blur

      setPriceErrors((prev) => {
        const copy = [...(prev || [])];
        copy[index] = err;
        return copy;
      });
    };

  const handlePriceBlur = (index: number, originalPrice: number) => {
    setNewPrices((prev) => {
      const copy = [...(prev || [])];
      if (Number.isNaN(copy[index]) || copy[index] <= 0) {
        copy[index] = originalPrice ?? 0; // Fallback to original (assume original > 0)
      }
      return copy;
    });

    // Re-validate after reversion
    setPriceErrors((prev) => {
      const copy = [...(prev || [])];
      const updatedPrice = newPrices[index]; // Use updated value
      let err: string | null = null;
      if (!Number.isNaN(updatedPrice) && updatedPrice <= 0) {
        err = "Price must be greater than 0";
      }
      copy[index] = err;
      return copy;
    });
  };
  // prevent invalid key presses (no e, +, -, ., etc.)
  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // allow: backspace, delete, arrows, tab
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];
    if (allowedKeys.includes(e.key)) return;

    // allow digits
    if (/^[0-9]$/.test(e.key)) return;

    // otherwise prevent
    e.preventDefault();
  };

  // prevent invalid paste
  const handlePricePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!/^\d+$/.test(text)) {
      e.preventDefault();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white/80">
        <div className="max-w-6xl mx-auto">
          <GoBackBtn className="mb-4 max-w-6xl mx-4" />
        </div>
      </div>
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Catalog Studio
                </h1>
                <p className="text-sm text-gray-600">Smart catalog creation</p>
              </div>
            </div>
            <div className="w-full md:w-fit flex items-center justify-between md:justify-end gap-2 md:gap-4">
              {["Template", "Details", "Review"].map((label, index) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-16 md:w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step > index + 1
                        ? "bg-emerald-500 text-white"
                        : step === index + 1
                          ? "bg-indigo-600 text-white shadow-lg"
                          : "bg-gray-200 text-gray-500"
                        }`}
                    >
                      {step > index + 1 ? (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                  </div>
                  {index < 2 && (
                    <div
                      className={`w-10 h-0.5 ${step > index + 1 ? "bg-emerald-500" : "bg-gray-200"
                        }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center">
              <img src={infiniteLoader} alt="Loading..." className="w-[14%]" />
            </div>
          ) : error && !isDownloading ? (
            <ErrorFallback
              message={error}
              onRetry={handleErrorState}
              heading={
                isStatus409 ? "Catalog Generation Limit Reached" : "Oops!"
              }
              buttonText={isStatus409 ? "Go To Profile" : "Retry!"}
            />
          ) : (
            <div>
              {step === 1 && (
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      Choose Your Template
                    </h2>
                    <p className="text-gray-600">
                      Select a theme and layout for your catalog
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Theme Style
                      </h3>

                      <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        <div className=" grid gap-3 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 grid-cols-1">
                          {templates[0]?.previewOptions.map((option) => (
                            <button
                              key={option.themeCode}
                              onClick={() => {
                                setSelectedThemeCode(option.themeCode);
                                setCustomizationData((prev) => ({
                                  ...prev,
                                  themeCode: option.themeCode,
                                }));
                              }}
                              className={`p-4 rounded-xl border-2 transition-all text-left w-full ${selectedThemeCode === option.themeCode
                                ? "border-indigo-500 bg-indigo-50 shadow-md"
                                : "border-gray-200 hover:border-gray-300"
                                }`}
                            >
                              <div className="text-sm font-medium text-gray-800">
                                {getThemeName(option.themeCode)}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {option.themePreviews.length} layouts
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-800">
                          Layout Grid
                        </h3>
                        <div className="relative group">
                          <Tippy content="Max number of products you want on each page">
                            <InfoIcon className="w-5 h-5 cursor-pointer" />
                          </Tippy>
                        </div>
                      </div>

                      <div className="grid grid-flow-col justify-center align-middle bg-slate-100 gap-4 overflow-x-auto pb-3 custom-scrollbar -mx-2 px-2 sm:px-4 p-6">
                        {templates[0]?.previewOptions
                          .find((o) => o.themeCode === selectedThemeCode)
                          ?.themePreviews.map((preview) => (
                            <div
                              key={`${preview.gridOptions.columns}x${preview.gridOptions.rows}`}
                              onClick={() => {
                                setSelectedTemplateId(templates[0].id);
                                setSelectedGridConfig(preview.gridOptions);
                                setCustomizationData((prev) => ({
                                  ...prev,
                                  gridConfig: preview.gridOptions,
                                }));
                              }}
                              className={`cursor-pointer flex-shrink-0 group ${selectedTemplateId === templates[0].id &&
                                selectedGridConfig.columns ===
                                preview.gridOptions.columns &&
                                selectedGridConfig.rows ===
                                preview.gridOptions.rows
                                ? "ring-2 ring-indigo-500"
                                : ""
                                }`}
                            >
                              <div className="w-28 sm:w-32 lg:w-36 aspect-[210/297] rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-indigo-300 transition-all">
                                <img
                                  src={preview.previewImage}
                                  alt={`${preview.gridOptions.columns}x${preview.gridOptions.rows}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="text-center mt-2">
                                <div className="text-sm font-medium text-gray-800">
                                  {preview.gridOptions.columns}×
                                  {preview.gridOptions.rows}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {preview.gridOptions.columns! *
                                    preview.gridOptions.rows!}{" "}
                                  items/page
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="p-6">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      Catalog Details
                    </h2>
                    <p className="text-gray-600">
                      Add your brand information and preferences
                    </p>
                  </div>
                  <Formik
                    initialValues={{
                      companyName: customizationData.companyName || "",
                      catalogTitle: customizationData.catalogTitle || "",
                      footerText: customizationData.footerText || "",
                      description: customizationData.description || "",
                      displayPrice: customizationData.displayPrice || false,
                    }}
                    validate={validationSchema}
                    onSubmit={(values) => {
                      setCustomizationData((prev) => ({
                        ...prev,
                        companyName: values.companyName,
                        catalogTitle: values.catalogTitle,
                        footerText: values.footerText,
                        description: values.description,
                        displayPrice: values.displayPrice,
                      }));
                      nextStep();
                    }}
                  >
                    {({ values, setFieldValue, errors, touched }) => (
                      <Form className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="companyName"
                            maxLength={50}
                            className="w-full p-3 border rounded-lg focus:ring-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Your company name"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue("companyName", e.target.value);
                              setCustomizationData((prev) => ({
                                ...prev,
                                companyName: e.target.value,
                              }));
                            }}
                          />
                          <ErrorMessage
                            name="companyName"
                            component="p"
                            className="text-sm text-red-600"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Catalog Title{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="catalogTitle"
                            maxLength={50}
                            className="w-full p-3 border rounded-lg focus:ring-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Catalog title"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue("catalogTitle", e.target.value);
                              setCustomizationData((prev) => ({
                                ...prev,
                                catalogTitle: e.target.value,
                              }));
                            }}
                          />
                          <ErrorMessage
                            name="catalogTitle"
                            component="p"
                            className="text-sm text-red-600"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Footer Text
                          </label>
                          <Field
                            type="text"
                            name="footerText"
                            className="w-full p-3 border rounded-lg focus:ring-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Contact info or copyright"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue("footerText", e.target.value);
                              setCustomizationData((prev) => ({
                                ...prev,
                                footerText: e.target.value,
                              }));
                            }}
                          />
                          <ErrorMessage
                            name="footerText"
                            component="p"
                            className="text-sm text-red-600"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <Field
                            as="textarea"
                            name="description"
                            maxLength={255}
                            className="w-full p-3 border rounded-lg focus:ring-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                            rows={2}
                            placeholder="Catalog description"
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>
                            ) => {
                              setFieldValue("description", e.target.value);
                              setCustomizationData((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }));
                            }}
                          />
                          <ErrorMessage
                            name="description"
                            component="p"
                            className="text-sm text-red-600"
                          />
                        </div>

                        {is360Customizer && (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Options
                            </label>
                            <div className="flex items-center p-3 border border-gray-300 rounded-lg">
                              <input
                                type="checkbox"
                                name="displayPrice"
                                checked={values.displayPrice}
                                onChange={(e) => {
                                  setFieldValue(
                                    "displayPrice",
                                    e.target.checked
                                  );
                                  setCustomizationData((prev) => ({
                                    ...prev,
                                    displayPrice: e.target.checked,
                                  }));
                                }}
                                className="w-4 h-4 text-indigo-600 rounded cursor-pointer focus:ring-indigo-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                Show prices
                              </span>
                            </div>
                          </div>
                        )}
                      </Form>
                    )}
                  </Formik>
                  <div className="py-4">
                    <h3 className="block text-sm font-medium text-gray-700">
                      Choose a Logo from Your Library
                    </h3>
                    {isLogosLoading && !isLogosLoaded ? (
                      <div className="flex justify-center items-center">
                        <img
                          src={infiniteLoader}
                          alt="Loading..."
                          className="w-[12%]"
                        />
                      </div>
                    ) : (
                      <>
                        {!userLogos ? (
                          <div className="text-center py-12">
                            <ImagesIcon
                              size={48}
                              className="text-gray-400 mx-auto mb-4"
                            />
                            <p className="text-gray-500 text-lg">
                              No logos uploaded yet
                            </p>
                            <p className="text-gray-400 text-sm">
                              Start by adding your first logo
                            </p>
                          </div>
                        ) : (
                          <div className="flex gap-6 items-center">
                            <div className="flex gap-6 overflow-x-auto px-2">
                              {userLogos.map((logo: any, index: any) => (
                                <motion.div
                                  key={logo.id}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.1 }}
                                  onClick={() => selectLogo(logo)}
                                  className={`group flex-shrink-0 relative bg-white/50 backdrop-blur-sm rounded-2xl border transition-all duration-300 min-w-fit cursor-pointer my-3
                              ${selectedLogoId === logo.id
                                      ? "border-indigo-500 ring-4 ring-indigo-200 shadow-md"
                                      : "border-gray-200 hover:border-indigo-400 hover:shadow-sm"
                                    }`}
                                >
                                  <div className="aspect-square p-4 pb-2 checkerboard rounded-t-2xl">
                                    <img
                                      src={logo.logo}
                                      alt={logo.id}
                                      className="w-10 h-10 md:w-20 md:h-20 object-contain mx-auto"
                                    />
                                  </div>
                                  <div>
                                    <h3 className="text-center font-semibold text-gray-800 truncate px-2">
                                      {logo.name}
                                    </h3>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            <div className="flex justify-center items-center h-full">
                              <div className="my-2">
                                <button
                                  onClick={handleAddLogo}
                                  className="border-2 border-dashed flex items-center border-gray-300 rounded-xl py-8 px-5 md:py-9 md:px-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                                >
                                  <Plus className="text-gray-400 w-8 h-8 md:w-16 md:h-16" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="p-6">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      Review & Generate
                    </h2>
                    <p className="text-gray-600">
                      Review your selections and generate the catalog
                    </p>
                  </div>
                  <div
                    className={`grid ${is360Customizer ? "md:grid-cols-2" : "md:grid-cols-3"
                      } gap-6 mb-6`}
                  >
                    {/* Template Card */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 break-words">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-indigo-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Template
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Theme:</span>{" "}
                          {getThemeName(selectedThemeCode)}
                        </p>
                        <p>
                          <span className="font-medium">Layout:</span>{" "}
                          {selectedGridConfig.columns}×{selectedGridConfig.rows}
                        </p>
                      </div>
                    </div>

                    {/* Company Card */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100 break-words">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-emerald-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-1a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        Company
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Name:</span>{" "}
                          {customizationData.companyName || "Not set"}
                        </p>
                        <p>
                          <span className="font-medium">Title:</span>{" "}
                          {customizationData.catalogTitle || "Not set"}
                        </p>
                      </div>
                    </div>

                    {/* Products Card */}

                    <div
                      className={`bg-gradient-to-br ${is360Customizer ? "md:col-span-2" : ""
                        } from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100 break-words overflow-x-hidden`}
                    >
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-amber-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                        Products ({selectedProducts.length})
                      </h3>

                      <div className="space-y-2 text-sm max-h-40 overflow-y-auto overflow-x-hidden pr-1">
                        {selectedProducts.length === 0 ? (
                          <p className="text-gray-600">No products selected</p>
                        ) : (
                          selectedProducts.map((product, index) => {
                            const originalPrice = Number(
                              product.variants?.[0]?.price || 0
                            );
                            const displayValue =
                              !Number.isNaN(newPrices[index]) &&
                                newPrices[index] !== undefined
                                ? String(Math.trunc(newPrices[index]))
                                : "";

                            return (
                              <div
                                key={index}
                                className="flex flex-col sm:flex-row justify-between items-start sm:items-start gap-3 sm:gap-4 p-3 rounded-lg bg-white/50 border border-gray-100 hover:bg-gray-50/70 transition-all w-full min-w-0"
                              >
                                {/* Product Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-800 max-w-md ">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {product.itemType === ITEM_TYPES.PRODUCT_GROUP ? (
                                      <Badge className="bg-purple-100 text-purple-700 border-purple-200 flex items-center gap-1 w-fit py-0 px-2 h-5 text-[10px]">
                                        <Package className="w-2.5 h-2.5" />
                                        Curated Kit
                                      </Badge>
                                    ) : (
                                      getProductNameWithAttributes(product) || "No attributes"
                                    )}
                                  </div>
                                </div>

                                {/* Price Section */}
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto min-w-0">
                                  {/* Original price */}
                                  {is360Customizer && (
                                    <div className="flex items-center whitespace-nowrap flex-shrink-0">
                                      <span className="mr-1 text-sm text-gray-600 inline">
                                        Original Price:
                                      </span>
                                      <span
                                        className="font-mono tabular-nums inline-block text-right max-w-[6ch] overflow-hidden text-ellipsis"
                                        aria-hidden="true"
                                        style={{ minWidth: 0 }}
                                      >
                                        ₹{originalPrice}
                                      </span>
                                    </div>
                                  )}

                                  {is360Customizer &&
                                    customizationData.displayPrice && (
                                      <div className="relative flex flex-1 sm:flex-none flex-col items-stretch sm:items-start w-full sm:w-auto min-w-0">
                                        <Tippy
                                          content={
                                            "Please note that the updated price for this product would be shown on the generated catalog"
                                          }
                                        >
                                          <label
                                            htmlFor={`price-${index}`}
                                            className="text-sm mb-1 select-none flex justify-start items-center gap-1"
                                          >
                                            <Info className="w-5 h-5 text-blue-500" />{" "}
                                            Updated Price :
                                          </label>
                                        </Tippy>

                                        <input
                                          inputMode="numeric"
                                          pattern="\d*"
                                          min={1}
                                          id={`price-${index}`}
                                          value={displayValue}
                                          onChange={handlePriceChange(index)}
                                          onKeyDown={handlePriceKeyDown}
                                          onPaste={handlePricePaste}
                                          onBlur={() =>
                                            handlePriceBlur(
                                              index,
                                              originalPrice
                                            )
                                          }
                                          className={`w-full sm:w-36 max-w-full p-2 text-sm border rounded-lg focus:ring-2 transition-all bg-white/80 font-mono ${priceErrors[index]
                                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                                            : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                                            }`}
                                          placeholder="0"
                                          aria-invalid={!!priceErrors[index]}
                                          aria-describedby={`price-error-${index}`}
                                          required
                                        />

                                        <div className="h-5 mt-1">
                                          {priceErrors[index] ? (
                                            <p
                                              id={`price-error-${index}`}
                                              className="text-[11px] text-red-600"
                                            >
                                              {priceErrors[index]}
                                            </p>
                                          ) : (
                                            <span className="text-[11px] text-transparent">
                                              &nbsp;{" "}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <ButtonPrimary
                      onClick={generatePDF}
                      loading={isDownloading}
                      disabled={selectedProducts.length === 0 || isLoading}
                      sizeClass="md"
                      className="max-w-fit"
                    >
                      <NotebookText className="w-5 h-5" />
                      Generate Catalog
                    </ButtonPrimary>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center px-1 md:px-6 py-4 bg-gray-50 border-t border-gray-200">
                <ButtonSecondary
                  onClick={prevStep}
                  disabled={step === 1}
                  variant="info"
                  sizeClass="!px-4 !py-3"
                  className={`flex items-center gap-2 rounded-lg ${step === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-100 shadow-sm"
                    }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </ButtonSecondary>
                <div className="flex items-center gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${step >= i ? "bg-indigo-600" : "bg-gray-300"
                        }`}
                    />
                  ))}
                </div>
                <div className="flex-shrink-0">
                  {step !== 3 ? (
                    <ButtonPrimary
                      onClick={nextStep}
                      disabled={!isStepValid() || step === 3}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium w-auto transition-colors ${isStepValid()
                        ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </ButtonPrimary>
                  ) : (
                    <div className="w-32 h-10 invisible"> &nbsp;</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <NcModal
        isOpenProp={isModalOpen}
        onCloseModal={() => setIsModalOpen(false)}
        modalTitle=""
        contentExtraClass="max-w-md"
        renderContent={() => (
          <GenerationModalContent
            isGenerating={isDownloading}
            generationMessages={catalogGenerationMessages}
            error={error}
            onSave={() => Promise.resolve()}
            onClose={() => setIsModalOpen(false)}
            generationItemName="catalog"
            messageIndex={messageIndex}
            setMessageIndex={setMessageIndex}
          />
        )}
      />
      {logoModalVisible && <LogoUploadeModel onClose={closeAddLogoDialogue} />}
    </div>
  );
};

export default CatalogGenerationPage;
